/** An error carrying the HTTP status and server-provided message. */
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** Wordt aangeroepen zodra de server laat weten dat de sessie er niet meer is.
 *  De AuthProvider hangt zich hieraan op en zet de gebruiker op `null`, waarna
 *  ProtectedRoute de redirect naar /login doet — zo hoeft deze module niets van
 *  de router of van React te weten. */
let onSessionLost: (() => void) | null = null;

export function setSessionLostHandler(fn: (() => void) | null) {
  onSessionLost = fn;
}

/** Een 401 op de auth-routes zelf hoort bij het inloggen (verkeerd wachtwoord)
 *  en betekent juist niet dat een bestaande sessie is weggevallen. Die moet op
 *  het formulier belanden, niet in een uitlog. */
const isAuthRoute = (path: string) => path.startsWith('/auth/');

/**
 * Of de laatste poging de server bereikte.
 *
 * `navigator.onLine` is hier niet genoeg: na een herlaadactie zonder bereik
 * meldt Chrome gewoon `true`, en dan zou de app zwijgen op het moment dat de
 * melding het meest nodig is (in de winkel). Een mislukte fetch is het echte
 * signaal — die faalt met een TypeError, in tegenstelling tot een HTTP-fout.
 */
let bereikbaar = true;
const kijkers = new Set<(bereikbaar: boolean) => void>();

export function volgBereikbaarheid(fn: (bereikbaar: boolean) => void): () => void {
  kijkers.add(fn);
  return () => kijkers.delete(fn);
}

export const isBereikbaar = () => bereikbaar;

function meldBereikbaarheid(nieuw: boolean) {
  if (nieuw === bereikbaar) return;
  bereikbaar = nieuw;
  for (const fn of kijkers) fn(nieuw);
}

async function request<T>(method: Method, path: string, body?: unknown): Promise<T> {
  const opts: RequestInit = { method, credentials: 'include', headers: {} };
  if (body !== undefined) {
    opts.headers = { 'Content-Type': 'application/json' };
    opts.body = JSON.stringify(body);
  }
  let res: Response;
  try {
    res = await fetch(`/api${path}`, opts);
  } catch (err) {
    // Netwerkfout, geen serverfout. De service worker heeft een schrijfactie
    // ondertussen in de wachtrij gezet; dit is puur om het te kunnen melden.
    meldBereikbaarheid(false);
    throw err;
  }
  meldBereikbaarheid(true);
  const text = await res.text();

  // De Worker antwoordt altijd met JSON, maar niet alles wat antwoordt is de
  // Worker: een Cloudflare-foutpagina (502, 522, 1015) of een captive portal op
  // het wifi-netwerk stuurt HTML terug. Zonder deze vangst gooide `JSON.parse`
  // een SyntaxError vóór de statuscontrole hieronder — je zag dan "Er ging iets
  // mis" in plaats van de echte fout, en bij een 401 werd `onSessionLost` nooit
  // aangeroepen, zodat de app op een dood scherm bleef staan in plaats van naar
  // het inlogscherm te gaan. De body is in dat geval onbruikbaar, de status niet.
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text) as unknown;
    } catch {
      if (res.ok) throw new ApiError(res.status, 'Onverwacht antwoord van de server');
    }
  }

  if (!res.ok) {
    if (res.status === 401 && !isAuthRoute(path)) onSessionLost?.();
    const err = data as { error?: string; details?: unknown } | null;
    throw new ApiError(res.status, err?.error ?? res.statusText, err?.details);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
};
